<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Businesses
            </h1>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Manage your client businesses
            </p>
          </div>
          <button
            @click="openCreateForm"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
            <svg class="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Add Business
          </button>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Filters -->
      <div class="flex items-center justify-between mb-6">
        <div class="flex-1 max-w-sm">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search businesses..."
            class="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            @input="debouncedSearch">
        </div>
        <div class="flex items-center space-x-2 ml-4">
          <button
            v-for="filter in archiveFilters"
            :key="filter.value"
            @click="archiveFilter = filter.value; fetchBusinesses()"
            :class="[
              'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
              archiveFilter === filter.value
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            ]">
            {{ filter.label }}
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div v-for="n in 6" :key="n" class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 animate-pulse">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
              <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-3" />
              <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
            </div>
            <div class="h-12 w-12 bg-gray-200 dark:bg-gray-700 rounded-lg ml-4" />
          </div>
        </div>
      </div>

      <!-- Grid -->
      <div v-else-if="businesses.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <BusinessCard
          v-for="business in businesses"
          :key="business.id"
          :business="business"
          @click="navigateToBusiness"
          @edit="openEditForm" />
      </div>

      <!-- Empty -->
      <div v-else class="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
          No businesses
        </h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Get started by adding your first business.
        </p>
        <div class="mt-6">
          <button
            @click="openCreateForm"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700">
            <svg class="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Add Business
          </button>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="mt-6 flex items-center justify-between">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Page {{ currentPage }} of {{ totalPages }} ({{ totalCount }} total)
        </p>
        <div class="flex space-x-2">
          <button
            :disabled="currentPage <= 1"
            @click="currentPage--; fetchBusinesses()"
            class="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
            Previous
          </button>
          <button
            :disabled="currentPage >= totalPages"
            @click="currentPage++; fetchBusinesses()"
            class="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
            Next
          </button>
        </div>
      </div>
    </div>

    <UiSlidePanel ref="slidePanel" />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { hideLoadingSpinner } from '~/javascript/utils/hide_loader.js'
import { getBusinesses } from '~/javascript/api/businesses.js'
import BusinessCard from '~/javascript/components/dashboard/components/business_card.vue'
import { UiSlidePanel } from '~/javascript/components/ui/slide_panel'
import BusinessForm from './form.vue'

const businesses = ref([])
const loading = ref(true)
const searchQuery = ref('')
const archiveFilter = ref('active')
const currentPage = ref(1)
const totalPages = ref(1)
const totalCount = ref(0)
const slidePanel = ref(null)

const archiveFilters = [
  { label: 'Active', value: 'active' },
  { label: 'Archived', value: 'archived' },
  { label: 'All', value: 'all' }
]

const fetchBusinesses = async() => {
  try {
    loading.value = true
    const params = { page: currentPage.value }
    if (searchQuery.value) params.q = searchQuery.value
    if (archiveFilter.value === 'active') params.archived = 'false'
    else if (archiveFilter.value === 'archived') params.archived = 'true'

    const response = await getBusinesses(params)
    businesses.value = response.data
    totalPages.value = parseInt(response.headers['total-pages'] || '1', 10)
    totalCount.value = parseInt(response.headers['total-count'] || '0', 10)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching businesses:', error)
  } finally {
    loading.value = false
  }
}

let searchTimeout = null
const debouncedSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    currentPage.value = 1
    fetchBusinesses()
  }, 300)
}

const navigateToBusiness = (business) => {
  window.location.href = `/businesses/${business.id}`
}

const onBusinessSaved = () => {
  slidePanel.value.close()
  fetchBusinesses()
}

const openCreateForm = () => {
  slidePanel.value.open({
    component: BusinessForm,
    props: {},
    events: { saved: onBusinessSaved }
  })
}

const openEditForm = (business) => {
  slidePanel.value.open({
    component: BusinessForm,
    props: { business },
    events: { saved: onBusinessSaved }
  })
}

onMounted(() => {
  hideLoadingSpinner()
  fetchBusinesses()
})
</script>
